import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { getTimezoneAbbr, parseDateTimeInUserTimezone } from 'src/utils/format-date-time';

type Params = {
    lead: ILead;
    /** Display name for the lead, e.g. from LeadUtils.getLeadName */
    leadName: string;
    propertyName?: string;
    upcomingShowing?: IShowing;
    timezoneOverride?: string | null;
};

const findMoveInDate = (lead: ILead): string | null => {
    for (const [key, value] of Object.entries(lead.smart_fields_data || {})) {
        if (typeof value === 'string' && /move.?in/i.test(key)) {
            return value;
        }
    }

    return null;
};

/**
 * Format lead details as plain text for sharing (e.g. copying to clipboard).
 * Sections with no data are omitted.
 */
export const formatLeadShareText = ({
    lead,
    leadName,
    propertyName,
    upcomingShowing,
    timezoneOverride,
}: Params): string => {
    const sections: string[] = [`Lead Name: ${leadName}`];

    const contactLines: string[] = [];
    if (lead.email) {
        contactLines.push(`* email: ${lead.email}`);
    }
    if (lead.phone_number) {
        contactLines.push(`* phone: ${lead.phone_number}`);
    }
    if (contactLines.length > 0) {
        sections.push(['Contact Information', ...contactLines].join('\n'));
    }

    if (propertyName) {
        sections.push(`Property Interest\n${propertyName}`);
    }

    const moveInDate = findMoveInDate(lead);
    if (moveInDate) {
        sections.push(`Lead Info\n* move-in date: ${moveInDate}`);
    }

    const notes = lead.notes?.contents?.trim();
    if (notes) {
        sections.push(`Notes\n${notes}`);
    }

    if (upcomingShowing) {
        const showingDate = parseDateTimeInUserTimezone(upcomingShowing.start_time, timezoneOverride);
        const timezone = timezoneOverride ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzAbbr = timezone ? ` ${getTimezoneAbbr(timezone)}` : '';
        sections.push(`Tour Info\nTime: ${showingDate.format('ddd, MMM D, YYYY')} at ${showingDate.format('h:mm A')}${tzAbbr}`);
    }

    return sections.join('\n\n');
};
